/**
 * WHIP brand mark, official asset (orange W glyph with "i" accent).
 *
 * - <WhipLogo />                    full lockup (icon + WHIP wordmark, optional tagline)
 * - <WhipLogo iconOnly />           just the W glyph (for favicons / mobile nav / avatars)
 * - <WhipLogo useLockupImage />     use the pre-rendered lockup PNG (W · WHIP · tagline)
 *
 * The raw PNG assets live in `/public/brand/` so they're served from the CDN.
 * `size` is the TARGET HEIGHT in px; width scales to preserve aspect.
 */

const ICON_SRC = "/brand/whip-icon.png";          // 512×309 PNG (transparent bg)
const LOCKUP_SRC = "/brand/whip-lockup.png";      // 3833×640 PNG (W | WHIP | tagline)

const ICON_ASPECT = 512 / 309;    // ≈ 1.657
const LOCKUP_ASPECT = 3833 / 640; // ≈ 5.989

const WhipMarkImg = ({ size = 28 }) => (
  <img
    src={ICON_SRC}
    alt=""
    aria-hidden="true"
    width={Math.round(size * ICON_ASPECT)}
    height={size}
    draggable="false"
    className="block flex-shrink-0 select-none"
    style={{ height: size, width: Math.round(size * ICON_ASPECT) }}
  />
);

export const WhipLogo = ({ iconOnly = false, size = 28, showTagline = false, useLockupImage = false }) => {
  if (iconOnly) {
    return (
      <span data-testid="whip-icon" className="inline-flex items-center">
        <WhipMarkImg size={size} />
      </span>
    );
  }

  // Pre-rendered horizontal lockup image (W + divider + WHIP + tagline baked in).
  // Use when you want the exact official artwork at a known height.
  if (useLockupImage) {
    return (
      <img
        src={LOCKUP_SRC}
        alt="WHIP. Know the vote before it happens"
        data-testid="whip-logo-lockup"
        draggable="false"
        className="block select-none"
        style={{ height: size, width: Math.round(size * LOCKUP_ASPECT) }}
      />
    );
  }

  // Default: PNG glyph + live text wordmark (matches nav spacing, stays crisp).
  return (
    <span
      data-testid="whip-logo"
      className="inline-flex items-center gap-2.5 select-none"
    >
      <WhipMarkImg size={size} />
      <span className="flex flex-col leading-none">
        <span
          className="relative text-white font-black tracking-[0.18em]"
          style={{ fontSize: Math.max(14, size * 0.72) }}
        >
          WHIP
          <sup
            aria-hidden="true"
            className="absolute font-bold text-white/75"
            style={{ fontSize: "0.42em", lineHeight: 1, top: "0.22em", right: "-1.55em", letterSpacing: "0.04em" }}
          >
            TM
          </sup>
        </span>
        <span
          aria-label="Intelligence"
          data-testid="whip-intelligence-subtitle"
          className="flex justify-between w-full uppercase text-orange-400/90 font-semibold mt-[3px]"
          style={{ fontSize: Math.max(6, size * 0.245), letterSpacing: 0 }}
        >
          {"INTELLIGENCE".split("").map((c, i) => (
            <span key={i}>{c}</span>
          ))}
        </span>
        {showTagline && (
          <span className="text-[10px] tracking-[0.22em] mt-1 text-white/40 uppercase">
            Know the vote before it happens
          </span>
        )}
      </span>
    </span>
  );
};

export default WhipLogo;
